import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Breadcrumbs,
  MenuItem,
  Link,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, UploadFile, Close, Delete } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Formik,
  Form,
  Field,
  FormikProps,
  FieldArray,
  FormikErrors,
} from "formik";
import * as Yup from "yup";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { useToast } from "../../components/Toast";
import { uploadFile } from "../../utils/UploadFile";
import { LocalUser } from "../../utils/UploadFile";
import NavigationHeader from "../../components/NavigationHeader";
import { getValidToken } from "../../utils/authUtils";
import { fetchDocuments } from "../../utils/FetchDocuments";
import {
  commonButtonProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../utils/styles";
import SearchableAutocomplete from "../../components/SearchableAutocomplete";

interface VehicleData {
  vehicleType: string;
  acquisitionType: string;
  vehicleNumber: string;
  makeYear: string;
  model: string;
  color: string;
  chassisNumber: string | null;
  acType: string;
  bodyType: string;
  configuration: string;
  fuelType: string;
  seatingCapacity: string;
  vehicleImages: (File | any)[];
  vehicleDocs: { file: File; docType: string }[];
  firstServiceDue: string;
  maintenanceInterval: string;
  nextMaintenanceDate: string;
  warrantyPeriod: string;
  lastServiceDate: string;
  lastServiceOdometer: string;
  knownMaintenanceIssues: string;
  rcNumber: string;
  rcExpiryDate: string;
  fastTagProvider: string;
  fastTagNumber: string;
  otherFastTagProvider: string;
  allUploads: any[];
  isNew: boolean;
  companyId: string;
}

export interface OutsourcedOwner {
  id?: string;
  companyName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  gstPercentage: number;
  companyId: string;
}

interface VehicleFormData {
  outsourcedOwner: OutsourcedOwner | null;
  vehicleData: VehicleData[];
}

// Validation Schema
const validationSchema = Yup.object().shape({
  vehicleType: Yup.string().required("Vehicle type is required"),

  acquisitionType: Yup.string().required("Acquisition type is required"),

  vehicleNumber: Yup.string()
    .required("Vehicle number is required")
    .max(10, "Vehicle number cannot exceed 10 characters"),

  fuelType: Yup.string().required("Fuel type is required"),

  makeYear: Yup.string().when("acquisitionType", {
    is: (val: string) => val !== "Outsourced",
    then: (schema) => schema.required("Make year is required"),
    otherwise: (schema) => schema,
  }),

  model: Yup.string().when("acquisitionType", {
    is: (val: string) => val !== "Outsourced",
    then: (schema) => schema.required("Model is required"),
    otherwise: (schema) => schema,
  }),

  color: Yup.string().when("acquisitionType", {
    is: (val: string) => val !== "Outsourced",
    then: (schema) => schema.required("Color is required"),
    otherwise: (schema) => schema,
  }),

  chassisNumber: Yup.string()
    .when("acquisitionType", {
      is: (val: string) => val !== "Outsourced",
      then: (schema) => schema.required("Chassis number is required"),
      otherwise: (schema) => schema,
    })
    .max(20, "Chassis number cannot exceed 20 characters"),

  acType: Yup.string().when("acquisitionType", {
    is: (val: string) => val !== "Outsourced",
    then: (schema) => schema.required("AC type is required"),
    otherwise: (schema) => schema,
  }),

  seatingCapacity: Yup.number().when("acquisitionType", {
    is: (val: string) => val !== "Outsourced",
    then: (schema) =>
      schema
        .required("Seating capacity is required")
        .min(1, "Seating capacity must be at least 1")
        .max(999, "Seating capacity cannot exceed 999"),
    otherwise: (schema) => schema,
  }),

  configuration: Yup.string().when(["acquisitionType", "vehicleType"], {
    is: (acquisitionType: string, vehicleType: string) =>
      acquisitionType !== "Outsourced" && vehicleType !== "Car",
    then: (schema) => schema.required("Configuration is required"),
    otherwise: (schema) => schema,
  }),

  warrantyPeriod: Yup.number().when("acquisitionType", {
    is: "New Purchase",
    then: (schema) =>
      schema
        .min(1, "Warranty period must be at least 1 year")
        .max(999, "Warranty period cannot exceed 999 years"),
    otherwise: (schema) => schema,
  }),

  // Outsourced owner validation
  outsourcedOwner: Yup.object().when("acquisitionType", {
    is: "Outsourced",
    then: (schema) =>
      schema.shape({
        companyName: Yup.string().required("Company name is required"),
        primaryContactName: Yup.string().required("Owner name is required"),
        primaryContactNumber: Yup.string()
          .required("Owner contact number is required")
          .matches(/^\d{10}$/, "Contact number must be 10 digits"),
        gstPercentage: Yup.number()
          .required("GST percentage is required")
          .min(0, "GST percentage cannot be negative")
          .max(100, "GST percentage cannot exceed 100"),
      }),
    otherwise: (schema) =>
      schema.shape({
        companyName: Yup.string(),
        primaryContactName: Yup.string(),
        primaryContactNumber: Yup.string(),
        gstPercentage: Yup.number(),
      }),
  }),
});

const AddVehicle: React.FC = () => {
  const localUser = JSON.parse(sessionStorage.getItem("user") ?? "{}");
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const isEditMode = !!vehicleId;
  const [searchParams] = useSearchParams();
  const isOutsourced = searchParams.get("outsourced") === "true";

  const [isLoading, setIsLoading] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Initial form values
  const getInitialValues = (): VehicleFormData => {
    const companyId = localUser?.company?.id || localUser?.companyId || "";
    const initialValues = {
      outsourcedOwner: isOutsourced
        ? {
            companyName: "",
            primaryContactName: "",
            primaryContactNumber: "",
            gstPercentage: 0,
            companyId: companyId,
          }
        : null,
      vehicleData: [
        {
          vehicleType: "",
          acquisitionType: isOutsourced ? "Outsourced" : "",
          vehicleNumber: "",
          makeYear: "",
          model: "",
          color: "",
          chassisNumber: null,
          acType: "",
          bodyType: "",
          configuration: "",
          fuelType: "",
          seatingCapacity: "0",
          vehicleImages: [],
          vehicleDocs: [],
          firstServiceDue: "",
          maintenanceInterval: "",
          nextMaintenanceDate: "",
          warrantyPeriod: "",
          lastServiceDate: "",
          lastServiceOdometer: "",
          knownMaintenanceIssues: "",
          rcNumber: "",
          rcExpiryDate: "",
          fastTagProvider: "",
          fastTagNumber: "",
          otherFastTagProvider: "",
          allUploads: [],
          companyId: localUser?.company?.id || localUser?.companyId || "",
          isNew: true,
        },
      ],
    };
    return initialValues;
  };

  const [initialValues, setInitialValues] = useState<VehicleFormData>(
    getInitialValues()
  );

  useEffect(() => {
    if (vehicleId) {
      fetchVehicleDetails();
    }
  }, [vehicleId]);

  const fetchVehicleDetails = async () => {
    setIsLoading(true);
    try {
      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}vehicles/${vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        const vehicleData = response.data.data;

        // Set form data with proper type conversion and defaults
        const updatedFormData: VehicleFormData = {
          outsourcedOwner: vehicleData.outsourcedOwner,
          vehicleData: [
            {
              vehicleType: vehicleData.vehicleType ?? "",
              acquisitionType: vehicleData.acquisitionType ?? "",
              vehicleNumber: vehicleData.vehicleNumber ?? "",
              makeYear: vehicleData.makeYear?.toString() ?? "",
              model: vehicleData.model ?? "",
              color: vehicleData.color ?? "",
              chassisNumber: vehicleData.chassisNumber ?? null,
              acType: vehicleData.isAC ? "AC" : "Non-AC",
              bodyType: vehicleData.bodyType ?? "",
              configuration: vehicleData.configuration ?? "",
              fuelType: vehicleData.fuelType ?? "",
              seatingCapacity: vehicleData.seatingCapacity?.toString() ?? "",
              vehicleImages: vehicleData.vehicleImages ?? [],
              vehicleDocs: vehicleData.vehicleDocs ?? [],
              firstServiceDue: vehicleData.firstServiceDue
                ? new Date(vehicleData.firstServiceDue)
                    .toISOString()
                    .split("T")[0]
                : "",
              maintenanceInterval: vehicleData.maintenanceInterval ?? "",
              nextMaintenanceDate: vehicleData.nextMaintenanceDate
                ? new Date(vehicleData.nextMaintenanceDate)
                    .toISOString()
                    .split("T")[0]
                : "",
              warrantyPeriod: vehicleData.warrantyPeriod?.toString() ?? "",
              lastServiceDate: vehicleData.lastServiceDate
                ? new Date(vehicleData.lastServiceDate)
                    .toISOString()
                    .split("T")[0]
                : "",
              lastServiceOdometer:
                vehicleData.lastServiceOdometer?.toString() ?? "",
              knownMaintenanceIssues: vehicleData.knownMaintenanceIssues ?? "",
              rcNumber: vehicleData.rcNumber ?? "",
              rcExpiryDate: vehicleData.rcExpiryDate
                ? new Date(vehicleData.rcExpiryDate).toISOString().split("T")[0]
                : "",
              fastTagProvider: vehicleData.fastTagProvider ?? "",
              fastTagNumber: vehicleData.fastTagNumber ?? "",
              otherFastTagProvider: vehicleData.otherFastTagProvider ?? "",
              allUploads: vehicleData.allUploads ?? [],
              isNew: false, // Set to false for existing vehicles being edited
              companyId:
                vehicleData.companyId ??
                localUser?.company?.id ??
                localUser?.companyId ??
                "",
            },
          ],
        };

        // Fetch vehicle images
        const documents = await fetchDocuments({
          documentType: "vehicle-image",
          documentContext: "vehicle",
          documentTypeId: vehicleId as string,
        });

        updatedFormData.vehicleData[0].vehicleImages = documents || [];
        setInitialValues(updatedFormData);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const removeVehicleImage = (
    vehicleIndex: number,
    imageIndex: number,
    values: VehicleFormData,
    setFieldValue: any
  ) => {
    const vehicle = values.vehicleData[vehicleIndex];
    const updatedImages = vehicle.vehicleImages.filter(
      (_, i) => i !== imageIndex
    );
    const updatedUploads = vehicle.allUploads.filter(
      (upload, i) =>
        !(upload.documentType === "vehicle-image" && i === imageIndex)
    );
    setFieldValue(`vehicleData[${vehicleIndex}].vehicleImages`, updatedImages);
    setFieldValue(`vehicleData[${vehicleIndex}].allUploads`, updatedUploads);
  };

  const selectOptions = {
    vehicleType: ["Bus", "Truck", "Car"],
    acquisitionType: ["New Purchase", "Used Purchase", "Leased"],
    acType: ["AC", "Non-AC"],
    bodyType: ["Sleeper", "Seater", "Semi Sleeper"],
    fuelType: ["Diesel", "CNG", "Electric", "Petrol"],
  };

  const seaterConfigOption = ["2*1", "2*2", "2*3", "Other"];
  const sleeperConfigOption = ["1*1", "2*1", "2*2", "Other"];
  const semiSleeperConfigOption = ["2*1", "2*2", "Other"];

  const handleSubmit = async (values: VehicleFormData) => {
    setIsLoading(true);
    try {
      const token = await getValidToken();

      // Process each vehicle in the vehicleData array
      const processedVehicles = values.vehicleData.map((vehicle) => {
        const processedVehicle = {
          ...vehicle,
          isAC: vehicle.acType === "AC",
          fastTagProvider:
            vehicle.fastTagProvider === "Other"
              ? vehicle.otherFastTagProvider
              : vehicle.fastTagProvider,
        };

        // Set all empty date fields to null for this vehicle
        const dateFields = [
          "firstServiceDue",
          "nextMaintenanceDate",
          "lastServiceDate",
          "rcExpiryDate",
        ];
        dateFields.forEach((field) => {
          if (
            !processedVehicle[field as keyof typeof processedVehicle] ||
            processedVehicle[field as keyof typeof processedVehicle] === ""
          ) {
            (processedVehicle as any)[field] = null;
          }
        });

        // Convert string numbers back to numbers for API
        if (processedVehicle.seatingCapacity) {
          (processedVehicle as any).seatingCapacity = parseInt(
            processedVehicle.seatingCapacity
          );
        }
        if (processedVehicle.warrantyPeriod) {
          (processedVehicle as any).warrantyPeriod = parseInt(
            processedVehicle.warrantyPeriod
          );
        }
        if (processedVehicle.makeYear) {
          (processedVehicle as any).makeYear = parseInt(
            processedVehicle.makeYear
          );
        }
        if (processedVehicle.lastServiceOdometer) {
          (processedVehicle as any).lastServiceOdometer = parseInt(
            processedVehicle.lastServiceOdometer
          );
        }

        // Remove client-side only fields (keep companyId for API)
        const { vehicleImages, allUploads, isNew, ...vehicleDataForAPI } =
          processedVehicle;

        return vehicleDataForAPI;
      });

      // Prepare form data for submission
      const formDataToSubmit = {
        outsourcedOwner: values.outsourcedOwner,
        vehicleData: processedVehicles,
      };

      // Collect all uploads from all vehicles
      const allUploads = values.vehicleData.flatMap(
        (vehicle) => vehicle.allUploads
      );

      if (isEditMode) {
        // For edit mode, handle single vehicle
        const url = `${process.env.REACT_APP_API_HOST}vehicles/v2/update/${vehicleId}?isOutsourced=${isOutsourced}`;
        const dataForEdit = {
          outsourcedOwner: values.outsourcedOwner,
          vehicleData: processedVehicles[0],
        };
        const response = await axios.put(url, dataForEdit, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.data?.isSuccess) {
          // Upload files for the edited vehicle
          await Promise.all(
            allUploads.map((upload) =>
              uploadFile({
                ...upload,
                documentTypeId: vehicleId,
                companyId: localUser?.companyId,
              })
            )
          );

          showToast("Vehicle updated successfully!", "success");
          navigate(`/vehicles/${vehicleId}`);
        }
      } else {
        // For add mode, handle multiple vehicles
        const url = `${process.env.REACT_APP_API_HOST}vehicles/v2/create?isOutsourced=${isOutsourced}`;

        const response = await axios.post(url, formDataToSubmit, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.data?.isSuccess) {
          const createdVehicles = response?.data?.data;

          // Upload files for each created vehicle
          if (allUploads.length > 0 && createdVehicles?.length > 0) {
            // Map uploads to their respective vehicles
            let uploadIndex = 0;
            for (
              let vehicleIndex = 0;
              vehicleIndex < values.vehicleData.length;
              vehicleIndex++
            ) {
              const vehicleUploads =
                values.vehicleData[vehicleIndex].allUploads;
              const createdVehicle = createdVehicles[vehicleIndex];

              if (vehicleUploads.length > 0 && createdVehicle?.id) {
                await Promise.all(
                  vehicleUploads.map((upload) =>
                    uploadFile({
                      ...upload,
                      documentTypeId: createdVehicle.id,
                      companyId: localUser?.companyId,
                    })
                  )
                );
              }
            }
          }

          const message =
            values.vehicleData.length > 1
              ? `${values.vehicleData.length} vehicles added successfully!`
              : "Vehicle added successfully!";

          showToast(message, "success");

          // Navigate to the first created vehicle or vehicles list
          if (createdVehicles?.length === 1) {
            navigate(`/vehicles/${createdVehicles[0].id}?new=true`);
          } else {
            navigate("/vehicles");
          }
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.message === "Validation error") {
        const uniqueField = error?.response?.data?.errors[0]?.field;
        showToast(`${uniqueField} is already been used`, "error");
      } else {
        showToast(
          isEditMode
            ? "Failed to update vehicle. Please try again."
            : "Failed to add vehicle(s). Please try again.",
          "error"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title={isEditMode ? "Edit Vehicle" : "Add Vehicle"}
        breadcrumbs={[
          { label: "Vehicle Fleet", path: "/vehicles" },
          { label: isEditMode ? "Edit Vehicle" : "Add Vehicle" },
        ]}
      />
      <Formik
        initialValues={initialValues}
        // validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }: FormikProps<VehicleFormData>) => (
          <Form>
            {isOutsourced && !isEditMode && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Owner Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <SearchableAutocomplete
                      entityType="OUTSOURCED_OWNER"
                      label="Company Name"
                      value={values?.outsourcedOwner?.companyName || ""}
                      onSelect={(option) => {
                        if (option) {
                          // When a complete option is selected, populate all fields
                          const selectedOwner = option as any;
                          setFieldValue("outsourcedOwner", {
                            id: selectedOwner.id,
                            companyName: selectedOwner.companyName || "",
                            primaryContactName: selectedOwner.name || "",
                            primaryContactNumber:
                              selectedOwner.contactNumber || "",
                            gstPercentage: selectedOwner.gstPercentage || 0,
                            companyId:
                              values?.outsourcedOwner?.companyId ||
                              localUser?.company?.id ||
                              localUser?.companyId ||
                              "",
                          });
                        } else {
                          // Clear all fields when null is selected
                          setFieldValue("outsourcedOwner", {
                            companyName: "",
                            primaryContactName: "",
                            primaryContactNumber: "",
                            gstPercentage: 0,
                            companyId:
                              values?.outsourcedOwner?.companyId ||
                              localUser?.company?.id ||
                              localUser?.companyId ||
                              "",
                          });
                          setFieldValue("outsourcedOwnerId", "");
                        }
                      }}
                      onChange={(value) => {
                        // When typing, only update the companyName field
                        setFieldValue("outsourcedOwner.companyName", value);
                      }}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? option
                          : option.companyName || ""
                      }
                      renderOptionContent={(option) => (
                        <>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {option.companyName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {option.contactNumber}
                          </Typography>
                        </>
                      )}
                      noOptionsText="No companies found"
                      disabled={false}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Owner Name"
                      name="outsourcedOwner.primaryContactName"
                      value={values?.outsourcedOwner?.primaryContactName || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      // error={
                      //   values.outsourcedOwner?.primaryContactName &&
                      //   Boolean(touched.outsourcedOwner?.primaryContactName) &&
                      //   Boolean(errors.outsourcedOwner?.primaryContactName)
                      // }
                      // helperText={
                      //   Boolean(touched.outsourcedOwner?.primaryContactName) &&
                      //   errors.outsourcedOwner?.primaryContactName
                      // }
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Owner Contact Number"
                      name="outsourcedOwner.primaryContactNumber"
                      value={values?.outsourcedOwner?.primaryContactNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      inputProps={{
                        maxLength: 10,
                      }}
                      // ?
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="GST Percentage"
                      name="outsourcedOwner.gstPercentage"
                      value={values?.outsourcedOwner?.gstPercentage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      type="number"
                      inputProps={{
                        min: 0,
                        max: 100,
                      }}
                      // error={
                      //   touched.outsourcedOwner?.gstPercentage &&
                      //   Boolean(errors.outsourcedOwner?.gstPercentage)
                      // }
                      // helperText={
                      //   touched.outsourcedOwner?.gstPercentage &&
                      //   errors.outsourcedOwner?.gstPercentage
                      // }
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}
            <FieldArray name="vehicleData">
              {({ push, remove }) => {
                const vehicleErrors = errors.vehicleData as
                  | FormikErrors<VehicleData>
                  | undefined;
                return (
                  <>
                    {values.vehicleData.map((vehicle, index) => (
                      <>
                        <Paper sx={{ p: 2, mb: 2 }}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography
                              variant="h6"
                              gutterBottom
                              fontSize="0.85rem"
                            >
                              {isOutsourced
                                ? `Vehicle Information #${index + 1}`
                                : "Vehicle Information"}
                            </Typography>
                            {vehicle.isNew && values.vehicleData.length > 1 && (
                              <IconButton
                                onClick={() => {
                                  remove(index);
                                }}
                              >
                                <Delete
                                  sx={{ fontSize: "0.85rem", color: "red" }}
                                />
                              </IconButton>
                            )}
                          </Box>
                          <Grid container spacing={2}>
                            {!isOutsourced && (
                              <Grid item xs={12} sm={2}>
                                <TextField
                                  select
                                  label="Acquisition Type"
                                  name={`vehicleData[${index}].acquisitionType`}
                                  value={vehicle.acquisitionType}
                                  onChange={(e) => {
                                    handleChange(e);
                                    // Reset dependent fields when acquisition type changes
                                    if (e.target.name === "acquisitionType") {
                                      setFieldValue(
                                        `vehicleData[${index}].firstServiceDue`,
                                        ""
                                      );
                                      setFieldValue(
                                        `vehicleData[${index}].maintenanceInterval`,
                                        "Select"
                                      );
                                      setFieldValue(
                                        `vehicleData[${index}].nextMaintenanceDate`,
                                        ""
                                      );
                                      setFieldValue(
                                        `vehicleData[${index}].warrantyPeriod`,
                                        ""
                                      );
                                      setFieldValue(
                                        `vehicleData[${index}].lastServiceDate`,
                                        ""
                                      );
                                      setFieldValue(
                                        `vehicleData[${index}].lastServiceOdometer`,
                                        ""
                                      );
                                      setFieldValue(
                                        `vehicleData[${index}].knownMaintenanceIssues`,
                                        ""
                                      );
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  required
                                  fullWidth
                                  error={
                                    touched.vehicleData?.[index]
                                      ?.acquisitionType &&
                                    Boolean(vehicleErrors?.acquisitionType)
                                  }
                                  helperText={
                                    touched.vehicleData?.[index]
                                      ?.acquisitionType &&
                                    vehicleErrors?.acquisitionType
                                  }
                                  {...commonTextFieldProps}
                                >
                                  <MenuItem
                                    value=""
                                    disabled
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    Select
                                  </MenuItem>
                                  {selectOptions.acquisitionType.map((opt) => (
                                    <MenuItem
                                      key={opt}
                                      value={opt}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      {opt}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            )}

                            <Grid item xs={12} sm={2}>
                              <TextField
                                select
                                label="Vehicle Type"
                                name={`vehicleData[${index}].vehicleType`}
                                value={vehicle.vehicleType}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                required
                                error={
                                  touched.vehicleData?.[index]?.vehicleType &&
                                  Boolean(vehicleErrors?.vehicleType)
                                }
                                helperText={
                                  touched.vehicleData?.[index]?.vehicleType &&
                                  vehicleErrors?.vehicleType
                                }
                                {...commonTextFieldProps}
                              >
                                <MenuItem
                                  value=""
                                  disabled
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  Select
                                </MenuItem>
                                {selectOptions.vehicleType.map((opt) => (
                                  <MenuItem
                                    key={opt}
                                    value={opt}
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                              <TextField
                                label="Vehicle Number (RC)"
                                inputProps={{
                                  title:
                                    "Vehicle number must be in format: MH01AA1111",
                                  maxLength: 10,
                                }}
                                name={`vehicleData[${index}].vehicleNumber`}
                                value={vehicle.vehicleNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                required
                                error={
                                  touched.vehicleData?.[index]?.vehicleNumber &&
                                  Boolean(vehicleErrors?.vehicleNumber)
                                }
                                helperText={
                                  touched.vehicleData?.[index]?.vehicleNumber &&
                                  vehicleErrors?.vehicleNumber
                                }
                                {...commonTextFieldProps}
                              />
                            </Grid>

                            <Grid item xs={12} sm={2}>
                              <TextField
                                select
                                label="Fuel Type"
                                name={`vehicleData[${index}].fuelType`}
                                value={vehicle.fuelType}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                required
                                error={
                                  touched.vehicleData?.[index]?.fuelType &&
                                  Boolean(vehicleErrors?.fuelType)
                                }
                                helperText={
                                  touched.vehicleData?.[index]?.fuelType &&
                                  vehicleErrors?.fuelType
                                }
                                {...commonTextFieldProps}
                              >
                                <MenuItem
                                  value="Select"
                                  disabled
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  Select
                                </MenuItem>
                                {selectOptions.fuelType.map((opt) => (
                                  <MenuItem
                                    key={opt}
                                    value={opt}
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            {/* Toggle Button for Additional Details - Only for Outsourced */}
                            {vehicle.acquisitionType === "Outsourced" && (
                              <Grid item xs={12}>
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    setShowAdditionalDetails(
                                      !showAdditionalDetails
                                    )
                                  }
                                  size={smallButtonProp.size}
                                  sx={{
                                    ...smallButtonProp.sx,
                                    color: "grey",
                                    borderColor: "grey.400",
                                    "&:hover": {
                                      borderColor: "grey.400",
                                      color: "grey",
                                    },
                                  }}
                                >
                                  {showAdditionalDetails
                                    ? "Hide Additional Details"
                                    : "Add Additional Details"}
                                </Button>
                              </Grid>
                            )}

                            {(vehicle.acquisitionType !== "Outsourced" ||
                              showAdditionalDetails) && (
                              <>
                                <Grid item xs={12} sm={2}>
                                  <TextField
                                    type="date"
                                    label="RC Start Date"
                                    name={`vehicleData[${index}].rcExpiryDate`}
                                    value={vehicle.rcExpiryDate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      touched.vehicleData?.[index]
                                        ?.rcExpiryDate &&
                                      Boolean(vehicleErrors?.rcExpiryDate)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]
                                        ?.rcExpiryDate &&
                                      vehicleErrors?.rcExpiryDate
                                    }
                                    {...commonTextFieldProps}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                  <TextField
                                    select
                                    label="Make Year"
                                    name={`vehicleData[${index}].makeYear`}
                                    value={vehicle.makeYear}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    required={
                                      vehicle.acquisitionType !== "Outsourced"
                                    }
                                    error={
                                      touched.vehicleData?.[index]?.makeYear &&
                                      Boolean(vehicleErrors?.makeYear)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]?.makeYear &&
                                      vehicleErrors?.makeYear
                                    }
                                    {...commonTextFieldProps}
                                  >
                                    <MenuItem
                                      value=""
                                      disabled
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      Select Year
                                    </MenuItem>
                                    {Array.from(
                                      {
                                        length: new Date().getFullYear() - 1989,
                                      },
                                      (_, i) => (
                                        <MenuItem
                                          key={1990 + i}
                                          value={String(1990 + i)}
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          {1990 + i}
                                        </MenuItem>
                                      )
                                    ).reverse()}
                                  </TextField>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    label="Model Name"
                                    name={`vehicleData[${index}].model`}
                                    value={vehicle.model}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    required={
                                      vehicle.acquisitionType !== "Outsourced"
                                    }
                                    error={
                                      touched.vehicleData?.[index]?.model &&
                                      Boolean(vehicleErrors?.model)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]?.model &&
                                      vehicleErrors?.model
                                    }
                                    {...commonTextFieldProps}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                  <TextField
                                    label="Color"
                                    name={`vehicleData[${index}].color`}
                                    value={vehicle.color}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    required={
                                      vehicle.acquisitionType !== "Outsourced"
                                    }
                                    error={
                                      touched.vehicleData?.[index]?.color &&
                                      Boolean(vehicleErrors?.color)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]?.color &&
                                      vehicleErrors?.color
                                    }
                                    {...commonTextFieldProps}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    label="Chassis Number"
                                    name={`vehicleData[${index}].chassisNumber`}
                                    value={vehicle.chassisNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    inputProps={{
                                      maxLength: 20,
                                    }}
                                    required={
                                      vehicle.acquisitionType !== "Outsourced"
                                    }
                                    error={
                                      touched.vehicleData?.[index]
                                        ?.chassisNumber &&
                                      Boolean(vehicleErrors?.chassisNumber)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]
                                        ?.chassisNumber &&
                                      vehicleErrors?.chassisNumber
                                    }
                                    {...commonTextFieldProps}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                  <TextField
                                    select
                                    label="AC / Non AC"
                                    name={`vehicleData[${index}].acType`}
                                    value={vehicle.acType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    required={
                                      vehicle.acquisitionType !== "Outsourced"
                                    }
                                    error={
                                      touched.vehicleData?.[index]?.acType &&
                                      Boolean(vehicleErrors?.acType)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]?.acType &&
                                      vehicleErrors?.acType
                                    }
                                    {...commonTextFieldProps}
                                  >
                                    <MenuItem
                                      value="Select"
                                      disabled
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      Select
                                    </MenuItem>
                                    {selectOptions.acType.map((opt) => (
                                      <MenuItem
                                        key={opt}
                                        value={opt}
                                        sx={{ fontSize: "0.75rem" }}
                                      >
                                        {opt}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                                {vehicle.vehicleType !== "Car" && (
                                  <>
                                    <Grid item xs={12} sm={2}>
                                      <TextField
                                        select
                                        label="Sleeper / Seater"
                                        name={`vehicleData[${index}].bodyType`}
                                        value={vehicle.bodyType}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                        error={
                                          touched.vehicleData?.[index]
                                            ?.bodyType &&
                                          Boolean(vehicleErrors?.bodyType)
                                        }
                                        helperText={
                                          touched.vehicleData?.[index]
                                            ?.bodyType &&
                                          vehicleErrors?.bodyType
                                        }
                                        {...commonTextFieldProps}
                                      >
                                        <MenuItem
                                          value="Select"
                                          disabled
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          Select
                                        </MenuItem>
                                        {selectOptions.bodyType.map((opt) => (
                                          <MenuItem
                                            key={opt}
                                            value={opt}
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            {opt}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <TextField
                                        select
                                        label="Configuration"
                                        name={`vehicleData[${index}].configuration`}
                                        value={vehicle.configuration}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                        disabled={
                                          !vehicle.bodyType ||
                                          vehicle.bodyType === "Select"
                                        }
                                        required={
                                          vehicle.acquisitionType !==
                                          "Outsourced"
                                        }
                                        error={
                                          touched.vehicleData?.[index]
                                            ?.configuration &&
                                          Boolean(vehicleErrors?.configuration)
                                        }
                                        helperText={
                                          touched.vehicleData?.[index]
                                            ?.configuration &&
                                          vehicleErrors?.configuration
                                        }
                                        {...commonTextFieldProps}
                                      >
                                        <MenuItem
                                          value="Select"
                                          disabled
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          Select
                                        </MenuItem>
                                        {(vehicle.bodyType === "Sleeper"
                                          ? sleeperConfigOption
                                          : vehicle.bodyType === "Semi Sleeper"
                                          ? semiSleeperConfigOption
                                          : seaterConfigOption
                                        ).map((opt) => (
                                          <MenuItem
                                            key={opt}
                                            value={opt}
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            {opt}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                  </>
                                )}

                                <Grid item xs={12} sm={2}>
                                  <TextField
                                    label="Seating Capacity"
                                    name={`vehicleData[${index}].seatingCapacity`}
                                    value={vehicle.seatingCapacity}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                    required={
                                      vehicle.acquisitionType !== "Outsourced"
                                    }
                                    type="number"
                                    error={
                                      touched.vehicleData?.[index]
                                        ?.seatingCapacity &&
                                      Boolean(vehicleErrors?.seatingCapacity)
                                    }
                                    helperText={
                                      touched.vehicleData?.[index]
                                        ?.seatingCapacity &&
                                      vehicleErrors?.seatingCapacity
                                    }
                                    inputProps={{
                                      min: "1",
                                      max: "999",
                                    }}
                                    {...commonTextFieldProps}
                                  />
                                </Grid>

                                <Grid
                                  container
                                  item
                                  xs={12}
                                  sm={12}
                                  spacing={2}
                                >
                                  {vehicle.acquisitionType ===
                                    "New Purchase" && (
                                    <>
                                      <Grid item xs={12} sm={2}>
                                        <TextField
                                          type="date"
                                          label="First Service Due at"
                                          name={`vehicleData[${index}].firstServiceDue`}
                                          value={vehicle.firstServiceDue}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          InputLabelProps={{ shrink: true }}
                                          fullWidth
                                          error={
                                            touched.vehicleData?.[index]
                                              ?.firstServiceDue &&
                                            Boolean(
                                              vehicleErrors?.firstServiceDue
                                            )
                                          }
                                          helperText={
                                            touched.vehicleData?.[index]
                                              ?.firstServiceDue &&
                                            vehicleErrors?.firstServiceDue
                                          }
                                          {...commonTextFieldProps}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={2}>
                                        <TextField
                                          select
                                          label="Scheduled Maintenance Interval"
                                          name={`vehicleData[${index}].maintenanceInterval`}
                                          value={vehicle.maintenanceInterval}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          fullWidth
                                          error={
                                            touched.vehicleData?.[index]
                                              ?.maintenanceInterval &&
                                            Boolean(
                                              vehicleErrors?.maintenanceInterval
                                            )
                                          }
                                          helperText={
                                            touched.vehicleData?.[index]
                                              ?.maintenanceInterval &&
                                            vehicleErrors?.maintenanceInterval
                                          }
                                          {...commonTextFieldProps}
                                        >
                                          <MenuItem
                                            value="Select"
                                            disabled
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            Select Type
                                          </MenuItem>
                                          <MenuItem
                                            value="Monthly"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            Monthly
                                          </MenuItem>
                                          <MenuItem
                                            value="Quarterly"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            Quarterly
                                          </MenuItem>
                                          <MenuItem
                                            value="Yearly"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            Yearly
                                          </MenuItem>
                                        </TextField>
                                      </Grid>
                                      <Grid item xs={12} sm={2}>
                                        <TextField
                                          type="date"
                                          label="Next Maintenance Due Date"
                                          name={`vehicleData[${index}].nextMaintenanceDate`}
                                          value={vehicle.nextMaintenanceDate}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          InputLabelProps={{ shrink: true }}
                                          fullWidth
                                          InputProps={{
                                            inputProps: {
                                              min:
                                                vehicle.firstServiceDue ||
                                                undefined,
                                            },
                                          }}
                                          error={
                                            touched.vehicleData?.[index]
                                              ?.nextMaintenanceDate &&
                                            Boolean(
                                              vehicleErrors?.nextMaintenanceDate
                                            )
                                          }
                                          helperText={
                                            touched.vehicleData?.[index]
                                              ?.nextMaintenanceDate &&
                                            vehicleErrors?.nextMaintenanceDate
                                          }
                                          {...commonTextFieldProps}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={2}>
                                        <TextField
                                          label="Warranty Period (Years)"
                                          name={`vehicleData[${index}].warrantyPeriod`}
                                          value={vehicle.warrantyPeriod}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          type="number"
                                          inputProps={{
                                            min: 0,
                                            max: 999,
                                          }}
                                          error={
                                            touched.vehicleData?.[index]
                                              ?.warrantyPeriod &&
                                            Boolean(
                                              vehicleErrors?.warrantyPeriod
                                            )
                                          }
                                          helperText={
                                            touched.vehicleData?.[index]
                                              ?.warrantyPeriod &&
                                            vehicleErrors?.warrantyPeriod
                                          }
                                          fullWidth
                                          {...commonTextFieldProps}
                                        />
                                      </Grid>
                                    </>
                                  )}

                                  {vehicle.acquisitionType !== "New Purchase" &&
                                    vehicle.acquisitionType !== "Select" && (
                                      <>
                                        <Grid item xs={12} sm={2}>
                                          <TextField
                                            type="date"
                                            label="Last Service Date"
                                            name={`vehicleData[${index}].lastServiceDate`}
                                            value={vehicle.lastServiceDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            error={
                                              touched.vehicleData?.[index]
                                                ?.lastServiceDate &&
                                              Boolean(
                                                vehicleErrors?.lastServiceDate
                                              )
                                            }
                                            helperText={
                                              touched.vehicleData?.[index]
                                                ?.lastServiceDate &&
                                              vehicleErrors?.lastServiceDate
                                            }
                                            {...commonTextFieldProps}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                          <TextField
                                            label="Last Service KMs Reading"
                                            name={`vehicleData[${index}].lastServiceOdometer`}
                                            value={vehicle.lastServiceOdometer}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            type="number"
                                            error={
                                              touched.vehicleData?.[index]
                                                ?.lastServiceOdometer &&
                                              Boolean(
                                                vehicleErrors?.lastServiceOdometer
                                              )
                                            }
                                            helperText={
                                              touched.vehicleData?.[index]
                                                ?.lastServiceOdometer &&
                                              vehicleErrors?.lastServiceOdometer
                                            }
                                            {...commonTextFieldProps}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                          <TextField
                                            type="date"
                                            label="Next Maintenance Due Date"
                                            name={`vehicleData[${index}].nextMaintenanceDate`}
                                            value={vehicle.nextMaintenanceDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            error={
                                              touched.vehicleData?.[index]
                                                ?.nextMaintenanceDate &&
                                              Boolean(
                                                vehicleErrors?.nextMaintenanceDate
                                              )
                                            }
                                            helperText={
                                              touched.vehicleData?.[index]
                                                ?.nextMaintenanceDate &&
                                              vehicleErrors?.nextMaintenanceDate
                                            }
                                            {...commonTextFieldProps}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                          <TextField
                                            label="Known Maintenance Issues"
                                            name={`vehicleData[${index}].knownMaintenanceIssues`}
                                            value={
                                              vehicle.knownMaintenanceIssues
                                            }
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            error={
                                              touched.vehicleData?.[index]
                                                ?.knownMaintenanceIssues &&
                                              Boolean(
                                                vehicleErrors?.knownMaintenanceIssues
                                              )
                                            }
                                            helperText={
                                              touched.vehicleData?.[index]
                                                ?.knownMaintenanceIssues &&
                                              vehicleErrors?.knownMaintenanceIssues
                                            }
                                            {...commonTextFieldProps}
                                          />
                                        </Grid>
                                      </>
                                    )}
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Paper>

                        {/* Vehicle Images - Separate Section */}
                        {vehicle.acquisitionType !== "Outsourced" && (
                          <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              fontSize="1.2rem"
                            >
                              Vehicle Images
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<UploadFile />}
                                  {...commonButtonProps}
                                >
                                  Upload Vehicle Images
                                  <input
                                    type="file"
                                    name={`vehicleData[${index}].vehicleImages`}
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={(e) => {
                                      const files = e.target.files;
                                      if (files) {
                                        const newImages = [
                                          ...vehicle.vehicleImages,
                                          ...Array.from(files),
                                        ];
                                        const newUploads = [
                                          ...vehicle.allUploads,
                                          ...Array.from(files).map((file) => ({
                                            file,
                                            documentType: "vehicle-image",
                                            documentContext: "vehicle",
                                            description: "",
                                          })),
                                        ];
                                        setFieldValue(
                                          `vehicleData[${index}].vehicleImages`,
                                          newImages
                                        );
                                        setFieldValue(
                                          `vehicleData[${index}].allUploads`,
                                          newUploads
                                        );
                                      }
                                    }}
                                  />
                                </Button>

                                {/* Preview Images */}
                                {vehicle.vehicleImages.length > 0 && (
                                  <Box
                                    mt={2}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={2}
                                  >
                                    {vehicle.vehicleImages.map(
                                      (file: any, imageIdx: number) => (
                                        <Box
                                          key={imageIdx}
                                          position="relative"
                                          sx={{
                                            border: "1px solid #ddd",
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            boxShadow: 1,
                                          }}
                                        >
                                          <img
                                            src={
                                              file.presignedUrl ||
                                              URL.createObjectURL(file)
                                            }
                                            alt={`preview-${imageIdx}`}
                                            width={120}
                                            height={90}
                                            style={{
                                              objectFit: "cover",
                                              display: "block",
                                            }}
                                          />
                                          <IconButton
                                            onClick={() => {
                                              removeVehicleImage(
                                                index,
                                                imageIdx,
                                                values,
                                                setFieldValue
                                              );
                                            }}
                                            size="small"
                                            sx={{
                                              position: "absolute",
                                              top: 4,
                                              right: 4,
                                              backgroundColor:
                                                "rgba(255, 255, 255, 0.8)",
                                              "&:hover": {
                                                backgroundColor:
                                                  "rgba(255, 255, 255, 0.9)",
                                              },
                                            }}
                                          >
                                            <Close fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                )}
                              </Grid>
                            </Grid>
                          </Paper>
                        )}
                      </>
                    ))}
                    {isOutsourced && !isEditMode && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        sx={{
                          mb: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() =>
                            push({
                              vehicleType: "",
                              acquisitionType: isOutsourced ? "Outsourced" : "",
                              vehicleNumber: "",
                              makeYear: "",
                              model: "",
                              color: "",
                              chassisNumber: null,
                              acType: "",
                              bodyType: "",
                              configuration: "",
                              fuelType: "",
                              seatingCapacity: "0",
                              vehicleImages: [],
                              vehicleDocs: [],
                              firstServiceDue: "",
                              maintenanceInterval: "",
                              nextMaintenanceDate: "",
                              warrantyPeriod: "",
                              lastServiceDate: "",
                              lastServiceOdometer: "",
                              knownMaintenanceIssues: "",
                              rcNumber: "",
                              rcExpiryDate: "",
                              fastTagProvider: "",
                              fastTagNumber: "",
                              otherFastTagProvider: "",
                              allUploads: [],
                              companyId:
                                localUser?.company?.id ||
                                localUser?.companyId ||
                                "",
                              isNew: true,
                            })
                          }
                          {...commonButtonProps}
                        >
                          Add another Vehicle
                        </Button>
                      </Box>
                    )}
                  </>
                );
              }}
            </FieldArray>

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={isSubmitting || isLoading}
                {...commonButtonProps}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting || isLoading}
                {...commonButtonProps}
              >
                {isSubmitting || isLoading
                  ? "Processing..."
                  : isEditMode
                  ? "Update Vehicle"
                  : "Add Vehicle"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default AddVehicle;
